import { Centrifuge } from 'centrifuge';
import type { Subscription, PublicationContext } from 'centrifuge';
import {
  makeObservable,
  observable,
  action,
  computed,
  runInAction
} from 'mobx';

import { WS_DOMAIN, WS_PATH } from 'widget/consts/api';

import type { RootStore } from '../RootStore/RootStore';

import { WsConnectionState, WS_CONFIG } from './types';
import type { WsChannelListener } from './types';

type PrivateFields =
  | '_connectionState'
  | '_authToken'
  | '_client'
  | '_subscription'
  | '_handleConnecting'
  | '_handleConnected'
  | '_handleDisconnected'
  | '_handleError';

export class WebSocketStore {
  private _rootStore: RootStore;
  private _client: Centrifuge | null = null;
  private _connectionState: WsConnectionState = WsConnectionState.DISCONNECTED;
  private _authToken: string | null = null;
  private _subscription: Subscription | null = null;
  private _isObtainingToken: boolean = false;

  constructor(rootStore: RootStore) {
    this._rootStore = rootStore;

    makeObservable<WebSocketStore, PrivateFields>(this, {
      _connectionState: observable,
      _authToken: observable,
      _client: observable.ref,
      _subscription: observable.ref,

      connectionState: computed,
      isConnected: computed,

      connect: action,
      disconnect: action,
      _handleConnecting: action,
      _handleConnected: action,
      _handleDisconnected: action,
      _handleError: action
    });
  }

  get connectionState(): WsConnectionState {
    return this._connectionState;
  }

  get isConnected(): boolean {
    return this._connectionState === WsConnectionState.CONNECTED;
  }

  private _obtainAuthToken = async (): Promise<void> => {
    if (this._isObtainingToken) {
      return;
    }

    this._isObtainingToken = true;

    try {
      const response = await this._rootStore.apiStore.obtainWsToken();

      if (!response.isError && response.data) {
        runInAction(() => {
          this._authToken = response.data.data.auth_token;
        });
      }
    } finally {
      runInAction(() => {
        this._isObtainingToken = false;
      });
    }
  };

  private _refreshToken = async (): Promise<string> => {
    await this._obtainAuthToken();
    return this._authToken || '';
  };

  connect = async (messageHandler: WsChannelListener): Promise<void> => {
    if (this._client) {
      if (this._connectionState === WsConnectionState.DISCONNECTED) {
        this._client.connect();
      }
      return;
    }

    if (!this._authToken) {
      await this._obtainAuthToken();
    }

    if (!this._authToken) {
      return;
    }

    const subscriptionResponse =
      await this._rootStore.apiStore.getSubscriptionToken();

    if (subscriptionResponse.isError || !subscriptionResponse.data) {
      return;
    }

    const {
      subscription_channel: subscriptionChannel,
      subscription_token: subscriptionToken
    } = subscriptionResponse.data.data;

    const wsUrl = `wss://${WS_DOMAIN}${WS_PATH}`;

    const client = new Centrifuge(wsUrl, {
      token: this._authToken,
      getToken: this._refreshToken,
      minReconnectDelay: WS_CONFIG.minReconnectDelay,
      maxReconnectDelay: WS_CONFIG.maxReconnectDelay,
      timeout: WS_CONFIG.timeout,
      maxServerPingDelay: WS_CONFIG.maxServerPingDelay
    });

    client.on('connecting', this._handleConnecting);
    client.on('connected', this._handleConnected);
    client.on('disconnected', this._handleDisconnected);
    client.on('error', this._handleError);

    runInAction(() => {
      this._connectionState = WsConnectionState.CONNECTING;
      this._client = client;
    });

    client.connect();

    const subscription = client.newSubscription(subscriptionChannel, {
      token: subscriptionToken
    });

    subscription.on('publication', (ctx: PublicationContext) => {
      try {
        messageHandler(ctx.data);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
      }
    });

    subscription.subscribe();

    runInAction(() => {
      this._subscription = subscription;
    });
  };

  disconnect = (): void => {
    if (this._subscription) {
      this._subscription.unsubscribe();
      this._subscription.removeAllListeners();
      this._subscription = null;
    }

    if (this._client) {
      this._client.removeAllListeners();
      this._client.disconnect();
      this._client = null;
      this._connectionState = WsConnectionState.DISCONNECTED;
    }
  };

  private _handleConnecting = (): void => {
    this._connectionState = WsConnectionState.CONNECTING;
  };

  private _handleConnected = (): void => {
    this._connectionState = WsConnectionState.CONNECTED;
  };

  private _handleDisconnected = (): void => {
    this._connectionState = WsConnectionState.DISCONNECTED;
  };

  private _handleError = (): void => {
    this._connectionState = WsConnectionState.ERROR;
  };

  destroy = (): void => {
    this.disconnect();
  };
}
